import { compactDateEs, shortDayMonthEs } from "./dateFormat";
import { friendlyTime12h } from "./scheduleGrid";

// Sección «Calendario del torneo» (Eduardo 2026-10-02, torneo 59): la barra
// de «Publicar horarios» por día, los días del torneo como chips, el resumen
// de «Ajustes de esta categoría», el botón de «Ordenar automático» y el
// filtro por día de «Partidos de la jornada». Lógica pura: se prueba sin
// montar la página.

type DatedMatch = { scheduled_date?: string | null; status?: string | null };

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** Un torneo de más días (una escalera de meses) solo muestra los días con partidos. */
const MAX_RANGE_DAYS = 31;

function isoDay(value: string | null | undefined): string | null {
  const day = (value ?? "").trim().slice(0, 10);
  return ISO_DAY.test(day) ? day : null;
}

function utcDay(iso: string): number {
  const [year, month, day] = iso.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function isoFromUtc(ms: number): string {
  const date = new Date(ms);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

/** Partidos (no cancelados) por fecha. */
export function matchCountByDate(matches: DatedMatch[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const match of matches) {
    const day = isoDay(match.scheduled_date);
    if (!day || match.status === "cancelled") continue;
    counts.set(day, (counts.get(day) ?? 0) + 1);
  }
  return counts;
}

export interface CalendarDayChip {
  date: string;
  /** Partidos de la categoría ese día (sin cancelados). */
  matches: number;
  /** Horarios sin publicar de la categoría ese día. */
  unpublished: number;
  isToday: boolean;
}

/** Días del torneo para los chips del calendario: del inicio al fin del
 * torneo (si dura hasta un mes), más cualquier día con partidos o con
 * horarios sin publicar, más el día en pantalla. En orden de fecha. */
export function tournamentDayChips(input: {
  startDate?: string | null;
  endDate?: string | null;
  matchesByDate: Map<string, number>;
  unpublishedByDate: Map<string, number>;
  selected: string;
  today: string;
}): CalendarDayChip[] {
  const dates = new Set<string>();
  const start = isoDay(input.startDate);
  if (start) {
    const endRaw = isoDay(input.endDate);
    const end = endRaw && endRaw >= start ? endRaw : start;
    const span = Math.round((utcDay(end) - utcDay(start)) / 86_400_000);
    if (span <= MAX_RANGE_DAYS) {
      for (let offset = 0; offset <= span; offset += 1) dates.add(isoFromUtc(utcDay(start) + offset * 86_400_000));
    }
  }
  for (const [date, count] of input.matchesByDate) if (count > 0 && isoDay(date)) dates.add(date);
  for (const [date, count] of input.unpublishedByDate) if (count > 0 && isoDay(date)) dates.add(date);
  const selected = isoDay(input.selected);
  if (selected) dates.add(selected);
  return [...dates]
    .sort((a, b) => a.localeCompare(b))
    .map((date) => ({
      date,
      matches: input.matchesByDate.get(date) ?? 0,
      unpublished: input.unpublishedByDate.get(date) ?? 0,
      isToday: date === input.today,
    }));
}

/** «vie 3 oct» → «Vie 3 oct» (título de la barra). */
export function dayTitleEs(date: string): string {
  const text = shortDayMonthEs(date) || date;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export type PublishBarState = "here" | "published" | "waiting" | "empty";

/** Estado de la barra del día cuando NO hay horarios sin publicar ese día
 * (con N > 0 la barra dice «Tienes N horarios sin publicar…»):
 * - torneo en borrador o con el candado del jugador: «Horarios listos…»,
 *   neutro (en verde parecía que los jugadores ya los ven);
 * - otros días de la categoría con pendientes: «Este día ya está
 *   publicado ✓» («Todo» se leía como toda la categoría);
 * - nada pendiente en ningún día: «Todo publicado ✓». */
export function publishBarIdle(input: {
  dayMatches: number;
  otherPendingDays: number;
  blocked: "draft" | "lock" | null;
}): { state: Exclude<PublishBarState, "here">; text: string } {
  if (input.dayMatches <= 0) return { state: "empty", text: "No hay horarios de este día." };
  if (input.blocked === "draft") {
    return { state: "waiting", text: "Horarios listos: se verán cuando se publique el torneo." };
  }
  if (input.blocked === "lock") return { state: "waiting", text: "Horarios listos: los jugadores aún no los ven." };
  return { state: "published", text: input.otherPendingDays > 0 ? "Este día ya está publicado ✓" : "Todo publicado ✓" };
}

/** Partes del chip de un día: «Vie» y «3 oct». */
export function dayChipParts(date: string): { weekday: string; day: string } {
  return compactDateEs(date) ?? { weekday: "", day: date };
}

/** «1 partido» / «6 partidos». */
export function partidosText(count: number): string {
  return count === 1 ? "1 partido" : `${count} partidos`;
}

/** Texto del botón de «Ordenar automático». */
export function programPendingLabel(count: number): string {
  if (count <= 0) return "Sin partidos pendientes";
  return count === 1 ? "Programar 1 partido pendiente" : `Programar ${count} partidos pendientes`;
}

/** «12:00 a 7:15 p. m.» (un solo «p. m.» si las dos horas lo llevan). */
export function timeRangeEs(start: string, end: string): string {
  const from = friendlyTime12h(start);
  const to = friendlyTime12h(end);
  const suffix = (text: string) => (text.endsWith("a. m.") ? "a. m." : text.endsWith("p. m.") ? "p. m." : "");
  if (suffix(from) && suffix(from) === suffix(to)) return `${from.slice(0, -suffix(from).length).trim()} a ${to}`;
  return `${from} a ${to}`;
}

function joinEs(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}

/** Una línea con los ajustes del calendario de la categoría abierta:
 * «12:00 a 7:15 p. m. · bloques de 75 min · 15 canchas · se guardan solos». */
export function calendarSettingsSummary(input: {
  startTime: string;
  /** Hora en que termina el último partido (último inicio + bloque). */
  lastEndTime: string;
  blockMinutes: string;
  courtCount: number;
  splitBases?: string[];
  /** Categoría de «Mini cancha» (el tipo de cancha se elige en estos ajustes). */
  miniCourt?: boolean;
  /** "division" = se guardan para la categoría; "view" = vista sin
   * categoría (no se guardan); "readonly" = sin aviso de guardado. */
  saveMode: "division" | "view" | "readonly";
}): string {
  const parts = [timeRangeEs(input.startTime, input.lastEndTime)];
  const block = Number(input.blockMinutes);
  parts.push(Number.isFinite(block) && block > 0 ? `bloques de ${block} min` : "bloque sin definir");
  parts.push(input.courtCount === 1 ? "1 cancha" : `${input.courtCount} canchas`);
  if (input.miniCourt) parts.push("mini cancha");
  const split = input.splitBases ?? [];
  if (split.length > 0) {
    parts.push(split.length === 1 ? `cancha ${split[0]} dividida` : `canchas ${joinEs(split)} divididas`);
  }
  if (input.saveMode === "division") parts.push("se guardan solos");
  else if (input.saveMode === "view") parts.push("sin categoría: no se guardan");
  return parts.join(" · ");
}

/** Filtro por día de «Partidos de la jornada»: todos, un día o los que no
 * tienen fecha (jugados sin horario). */
export type ListDayFilter = "all" | "none" | string;

export interface ListDayOption {
  value: ListDayFilter;
  label: string;
  count: number;
}

/** Chips del filtro por día: «Todos los días» y cada fecha con partidos
 * (con su cuenta); «Sin fecha» si hay jugados sin horario. El día elegido
 * siempre sale, aunque ya no tenga partidos (para poder quitarlo). */
export function listDayOptions(matches: DatedMatch[], selected: ListDayFilter): ListDayOption[] {
  const counts = new Map<string, number>();
  let withoutDate = 0;
  for (const match of matches) {
    const day = isoDay(match.scheduled_date);
    if (day) counts.set(day, (counts.get(day) ?? 0) + 1);
    else withoutDate += 1;
  }
  if (selected !== "all" && selected !== "none" && !counts.has(selected)) counts.set(selected, 0);
  const options: ListDayOption[] = [{ value: "all", label: "Todos los días", count: matches.length }];
  for (const [date, count] of [...counts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    options.push({ value: date, label: dayTitleEs(date), count });
  }
  if (withoutDate > 0 || selected === "none") options.push({ value: "none", label: "Sin fecha", count: withoutDate });
  return options;
}

/** Los partidos de la lista que caen en el día elegido. */
export function filterByListDay<T extends DatedMatch>(matches: T[], day: ListDayFilter): T[] {
  if (day === "all") return matches;
  if (day === "none") return matches.filter((match) => !isoDay(match.scheduled_date));
  return matches.filter((match) => isoDay(match.scheduled_date) === day);
}
